import { KeyboardNavigation } from 'Shared/keyboard_navigation';
import { initializeReactQuery } from 'Shared/react-query-initialize';
import { ViewLangIntlProvider } from 'Shared/use_intl';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { getSearchClient } from 'Guide/searchClient';
import { getGrowthbook } from 'Shared/features';
import { createTrackedRoot, ReadinessGateHold } from 'Shared/react-mount-tracker';
import { useEffect, useState } from 'react';

new KeyboardNavigation({
   keys: {
      'C-k': deviceNavigator,
      'M-k': deviceNavigator,
   },
});

// The one root the picker renders in, made on the first open and kept. A
// root per open would pile up: the modal's own close leaves it mounted, and
// nothing unmounts it. `open` is set by the host once it has mounted, and
// `isInFlight` covers the first open from the fetch to that mount, so a
// press in between does nothing rather than start a second mount.
const picker = { root: null, open: null, isInFlight: false };

// Ctrl-K and the home page's "Find your device" button open the picker. Its
// code (the modal dialog among it) is fetched on the first open, so the pages
// that load this file don't download it up front. The readiness gate covers
// the fetch. A picker already on the page, this one or another module's
// (choose_ask_device renders one on the ask page), means nothing to do.
export default async function deviceNavigator() {
   if (document.querySelector('[data-testid="devicePickerComponent"]')) return;
   if (picker.open) {
      picker.open();
      return;
   }
   if (picker.isInFlight) return;
   picker.isInFlight = true;
   const root = picker.root ?? createPickerRoot();
   // Holds the gate across the fetch; the picker's own render replaces it.
   root.render(<ReadinessGateHold />);
   try {
      const [{ DevicePickerModal }, growthbook] = await Promise.all([
         import('@ifixit/device-picker'),
         getGrowthbook(),
      ]);
      root.render(
         initializeReactQuery(
            <ViewLangIntlProvider>
               <GrowthBookProvider growthbook={growthbook}>
                  <DevicePickerHost DevicePickerModal={DevicePickerModal} />
               </GrowthBookProvider>
            </ViewLangIntlProvider>
         )
      );
   } catch (error) {
      root.render(null);
      picker.isInFlight = false;
      throw error;
   }
}

// Owns whether the picker is open, so React's own state says so rather than
// a flag this module has to keep in step with the modal. Mounts open: the
// first render is the first open.
function DevicePickerHost({ DevicePickerModal }) {
   const [isOpen, setIsOpen] = useState(true);
   useEffect(() => {
      picker.open = () => setIsOpen(true);
      picker.isInFlight = false;
      return () => {
         picker.open = null;
      };
   }, []);
   return (
      <DevicePickerModal
         isOpen={isOpen}
         onOpenChange={setIsOpen}
         onSelect={device => (window.location.href = device.url)}
         algoliaConfig={{
            searchClient: getSearchClient(),
            indexPrefix: App.algoliaConfig['indexPrefix'],
         }}
         oniFixit={App.which === 'ifixit'}
         langid={App.lang}
         storeLocale={App.userPreferredStoreLocale}
         treeNavigationProps={'showAreaButtons'}
         analyticsKey={'Navigator'}
      />
   );
}

function createPickerRoot() {
   const node = document.createElement('div');
   document.body.append(node);
   picker.root = createTrackedRoot(node, {
      // A render error unmounts the host without its cleanup, so the next
      // press would find a stale `open` and set state on nothing. Reset, so
      // it fetches and mounts again. The crashed tree never committed, so
      // its tracked mount never settled; rendering nothing settles it and
      // leaves the root empty. React would have reported the error itself;
      // this handler replaces that, so it reports the same way.
      onUncaughtError: (error, errorInfo) => {
         picker.open = null;
         picker.isInFlight = false;
         picker.root.render(null);
         if (typeof reportError === 'function') {
            reportError(error);
         } else {
            console.error(error, errorInfo.componentStack);
         }
      },
   });
   return picker.root;
}
